// ============================================
// HOOK - Barcode Scanner Global
// ============================================

import { useEffect, useRef, useCallback } from 'react';

interface UseBarcodeScanOptions {
  /**
   * Callback que se ejecuta cuando se detecta un código escaneado
   */
  onScan: (barcode: string) => void;
  
  /**
   * Tiempo máximo entre caracteres para considerar que es un escaneo (ms)
   * Las pistolas escriben muy rápido (~10-50ms entre caracteres)
   * Default: 100ms
   */
  scanTimeout?: number;
  
  /**
   * Longitud mínima del código para considerarlo válido
   * Default: 3
   */
  minLength?: number;
  
  /**
   * Tecla que termina el escaneo (usualmente Enter)
   * Default: 'Enter'
   */
  endKey?: string;
  
  /**
   * Si está habilitado el escaneo
   * Default: true
   */
  enabled?: boolean;

  /**
   * Si debe prevenir el comportamiento por defecto de las teclas
   * Default: true
   */
  preventDefault?: boolean;

  /**
   * Acepta además códigos de pack: "P-" seguido de letras, números, "." "_" o "-"
   * (ej. P-12312344, P-LECHEGALLETA). Sin esta opción solo se aceptan dígitos.
   * Default: false
   */
  permitirPrefijoPack?: boolean;
}

// Un código de pack empieza con "P-" (el backend lo guarda en mayúsculas); el resto del código
// puede llevar letras del nombre del pack.
const esCaracterDePack = (key: string, buffer: string): boolean => {
  if (key.length !== 1) return false;
  if (buffer === '') return /^[pP]$/.test(key);
  if (/^[pP]$/.test(buffer)) return key === '-';
  if (/^[pP]-/.test(buffer)) return /^[\p{L}\p{N}_.-]$/u.test(key);
  return false;
};

/**
 * Hook para detectar escaneos de códigos de barras de forma global
 * sin necesidad de que un input tenga el foco.
 * 
 * Detecta automáticamente cuando se usa una pistola de escaneo basándose en:
 * - Velocidad de escritura muy rápida (< 100ms entre caracteres)
 * - Finalización con tecla Enter
 * 
 * @example
 * ```tsx
 * useBarcodeScan({
 *   onScan: (barcode) => {
 *     console.log('Código escaneado:', barcode);
 *     // Buscar producto, agregar al carrito, etc.
 *   },
 *   enabled: true
 * });
 * ```
 */
export const useBarcodeScan = ({
  onScan,
  scanTimeout = 100,
  minLength = 3,
  endKey = 'Enter',
  enabled = true,
  preventDefault = true,
  permitirPrefijoPack = false,
}: UseBarcodeScanOptions) => {
  // Buffer para acumular los caracteres escaneados
  const barcodeBuffer = useRef<string>('');
  
  // Timestamp del último carácter recibido
  const lastKeyTime = useRef<number>(0);
  
  // Timer para limpiar el buffer
  const timeoutRef = useRef<number | null>(null);

  /**
   * Limpia el buffer y el timeout
   */
  const clearBuffer = useCallback(() => {
    barcodeBuffer.current = '';
    lastKeyTime.current = 0;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * Procesa el código escaneado
   */
  const processScan = useCallback(() => {
    const barcode = barcodeBuffer.current.trim();
    
    if (barcode.length >= minLength) {
      onScan(barcode);
    }
    
    clearBuffer();
  }, [onScan, minLength, clearBuffer]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    /**
     * Handler global de eventos de teclado
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      const currentTime = Date.now();
      const timeSinceLastKey = currentTime - lastKeyTime.current;
      
      // Las pistolas envían Shift antes de cada mayúscula: no deben cortar el escaneo del pack
      if (permitirPrefijoPack && event.key === 'Shift') {
        return;
      }

      // Solo permitir números (0-9) y, si se habilitó, el código de un pack ("P-...").
      // Un buffer vencido se descarta, así que el carácter se evalúa contra un buffer vacío.
      const bufferVigente = timeSinceLastKey > scanTimeout && lastKeyTime.current > 0
        ? ''
        : barcodeBuffer.current;
      const esCaracterValido = /^\d$/.test(event.key)
        || (permitirPrefijoPack && esCaracterDePack(event.key, bufferVigente));
      if (!esCaracterValido && event.key !== endKey) {
        // Limpiar buffer si se presiona una tecla no válida
        if (barcodeBuffer.current) {
          clearBuffer();
        }
        return;
      }
      
      // Detectar si el usuario está escribiendo en un input/textarea/editable
      const activeElement = document.activeElement;
      const isTypingInInput = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).isContentEditable
      );
      
      // Si ya hay un escaneo en progreso
      const scanInProgress = barcodeBuffer.current.length > 0;
      
      // Detectar velocidad de scanner: < 50ms entre caracteres
      const isFastTyping = timeSinceLastKey < 50 && lastKeyTime.current > 0;

      // Si es la tecla de finalización (Enter)
      if (event.key === endKey) {
        // Solo procesar si hay algo en el buffer y fue escrito rápido
        if (barcodeBuffer.current && timeSinceLastKey < scanTimeout) {
          // Prevenir el Enter para que no se dispare en el input
          if (preventDefault) {
            event.preventDefault();
            event.stopPropagation();
          }
          processScan();
        } else {
          clearBuffer();
        }
        return;
      }

      // Si pasó mucho tiempo desde el último carácter, limpiar buffer
      if (timeSinceLastKey > scanTimeout && lastKeyTime.current > 0) {
        clearBuffer();
      }

      // REGLA DE ORO: Solo interceptar si cumple AL MENOS UNA de estas condiciones:
      // 1. NO estás en un input (permite scanner desde cualquier lugar)
      // 2. Es velocidad de scanner (escritura muy rápida)
      // 3. Ya hay un escaneo en progreso (continuar capturando)
      
      const shouldIntercept = !isTypingInInput || isFastTyping || scanInProgress;
      
      // Si estás escribiendo normalmente en un input (lento, sin escaneo previo), permitir
      if (!shouldIntercept) {
        return;
      }

      // Capturar el carácter para el scanner
      barcodeBuffer.current += event.key;
      lastKeyTime.current = currentTime;
      
      // Prevenir el default para que no se escriba en inputs
      if (preventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Configurar timeout para limpiar el buffer si no llega Enter
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        // Si el buffer tiene contenido suficiente, procesarlo
        if (barcodeBuffer.current.length >= minLength) {
          processScan();
        } else {
          clearBuffer();
        }
      }, scanTimeout * 2);
    };

    // Agregar listener global
    document.addEventListener('keydown', handleKeyDown, true);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      clearBuffer();
    };
  }, [enabled, endKey, scanTimeout, minLength, preventDefault, permitirPrefijoPack, processScan, clearBuffer]);

  return {
    /**
     * Limpia manualmente el buffer (útil al cambiar de página)
     */
    clearBuffer,
  };
};
